/**
 * Storage utility for shop navigation state persistence
 * Handles saving and restoring shop scroll position for back button navigation
 */

export interface ShopScrollState {
  shopId: string
  scrollTop: number
  timestamp: number
}

const SHOP_SCROLL_KEY = 'dukkan_shop_scroll_position'
const MAX_STORAGE_AGE = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

/**
 * Save shop scroll position to localStorage
 * Used when user clicks a shop to remember where they are in the shop list
 */
export function saveShopScrollPosition(shopId: string, scrollTop: number): void {
  try {
    const state: ShopScrollState = {
      shopId,
      scrollTop,
      timestamp: Date.now()
    }
    localStorage.setItem(SHOP_SCROLL_KEY, JSON.stringify(state))
    console.log(`💾 [shopStorage] Saved scroll position for ${shopId}: ${scrollTop}px`)
  } catch (error) {
    console.warn(`⚠️  [shopStorage] Failed to save scroll position:`, error)
  }
}

/**
 * Restore shop scroll position from localStorage
 * Used when user returns to shop list after viewing a shop
 * Returns null if no saved state found or state is stale
 */
export function loadShopScrollPosition(): ShopScrollState | null {
  try {
    const stored = localStorage.getItem(SHOP_SCROLL_KEY)
    if (!stored) {
      console.log(`ℹ️  [shopStorage] No saved scroll position found`)
      return null
    }

    const state = JSON.parse(stored) as ShopScrollState
    
    // Check if stored state is stale (older than 24 hours)
    const age = Date.now() - state.timestamp
    if (age > MAX_STORAGE_AGE) {
      console.log(`ℹ️  [shopStorage] Saved scroll position is stale (${(age / 1000 / 60).toFixed(1)} minutes old), clearing`)
      localStorage.removeItem(SHOP_SCROLL_KEY)
      return null
    }

    console.log(`📂 [shopStorage] Loaded scroll position for ${state.shopId}: ${state.scrollTop}px`)
    return state
  } catch (error) {
    console.warn(`⚠️  [shopStorage] Failed to load scroll position:`, error)
    return null
  }
}

/**
 * Clear saved scroll position from localStorage
 */
export function clearShopScrollPosition(): void {
  try {
    localStorage.removeItem(SHOP_SCROLL_KEY)
    console.log(`🗑️  [shopStorage] Cleared saved scroll position`)
  } catch (error) {
    console.warn(`⚠️  [shopStorage] Failed to clear scroll position:`, error)
  }
}
